/**
 * Topbar scan-box slot — the shell's one globally focusable barcode field.
 *
 * THE RULE: the topbar does not own a scan box, it owns a SLOT. A screen that
 * scans registers one with `useRegisterScanBox`; the box appears while that
 * screen is mounted and vanishes with it. Finance never sees it.
 *
 *   floor screen ──useRegisterScanBox({ onScan, placeholder, hotkey })──┐
 *                                                                       ▼
 *   <ScanSlotProvider> (app-layout)  ──active registration──►  <TopbarScanBox/>
 *                                                              autofocus,
 *                                                              F2 / "/" hotkeys,
 *                                                              "listening" state,
 *                                                              re-focus after each scan
 *
 * This module deliberately does NOT import the scanner package. Capture policy
 * (inter-key timing, symbology validation, prefix stripping) belongs to
 * `@erp/scanner-input`; the slot only owns the field, its focus and its keys.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ScanLine } from "lucide-react";
import { Input, cn } from "@erp/ui";

/** Focus keys bound by default: F2 anywhere, "/" only when no field has focus. */
export const DEFAULT_SCAN_HOTKEYS: readonly string[] = ["F2", "/"];

const EMPTY_HOTKEYS: readonly string[] = [];

export interface ScanContext {
  /** The field's value before trimming — keep it if your parser strips its own prefix. */
  raw: string;
}

/**
 * How the box gets its value.
 *
 * - `"field"` (default): the box IS the capture target. A wedge's Enter
 *   terminator submits it, exactly like a human pressing Enter.
 * - `"external"`: your own `useScannerInput` (window-level capture with timing
 *   heuristics) delivers the scan; the box stays focused so keystrokes land
 *   somewhere harmless and shows what was typed. It never calls `onScan` —
 *   call `handle.clear()` when your source fires.
 */
export type ScanCaptureMode = "field" | "external";

export interface ScanBoxRegistration {
  /** Called with the trimmed field value on every completed scan. */
  onScan: (value: string, context: ScanContext) => void;
  /** Field placeholder — say what to scan ("Scan item or bin"). */
  placeholder?: string;
  /** Accessible name. Defaults to a generic "Barcode scan". */
  label?: string;
  /** One line of guidance, announced with the field and shown on wide screens. */
  hint?: string;
  /** Focus key(s). Default {@link DEFAULT_SCAN_HOTKEYS}; `null` or `[]` binds none. */
  hotkey?: string | readonly string[] | null;
  /** Registered but inert: visible, greyed, no autofocus, no hotkeys (e.g. while a dialog is open). */
  disabled?: boolean;
  /** See {@link ScanCaptureMode}. Default `"field"`. */
  captureMode?: ScanCaptureMode;
  /** Escape pressed in the box. The box clears itself either way. */
  onCancel?: () => void;
}

export interface ScanBoxHandle {
  /** True while THIS registration is the one the topbar renders. */
  isActive: boolean;
  /** True while the box has focus — keystrokes are landing in it right now. */
  isListening: boolean;
  /** Put focus back in the box (after closing a dialog, after an error toast). */
  focus: () => void;
  /** Empty the box and re-focus it. */
  clear: () => void;
}

/* -------------------------------------------------------------------------- */
/* Registry                                                                   */
/* -------------------------------------------------------------------------- */

interface ScanBoxDisplay {
  placeholder: string | undefined;
  label: string | undefined;
  hint: string | undefined;
  hotkeys: readonly string[];
  disabled: boolean;
  captureMode: ScanCaptureMode;
}

interface ScanBoxEntry {
  id: string;
  display: ScanBoxDisplay;
  /** Live handle to the caller's latest callbacks — inline closures stay safe. */
  config: { current: ScanBoxRegistration };
}

interface ScanBoxCommands {
  focus: () => void;
  clear: () => void;
}

interface ScanSlotStore {
  active: ScanBoxEntry | null;
  listening: boolean;
  register: (entry: ScanBoxEntry) => void;
  unregister: (id: string) => void;
  setCommands: (commands: ScanBoxCommands | null) => void;
  setListening: (listening: boolean) => void;
  focus: () => void;
  clear: () => void;
}

const NOOP: ScanSlotStore = {
  active: null,
  listening: false,
  register: () => {},
  unregister: () => {},
  setCommands: () => {},
  setListening: () => {},
  focus: () => {},
  clear: () => {},
};

/**
 * Default is a working no-op registry, not `null`: a floor screen rendered
 * outside the shell (a unit test, a Storybook-style harness) must not crash —
 * it simply has nowhere to put its box.
 */
const ScanSlotContext = createContext<ScanSlotStore>(NOOP);

export interface ScanSlotProviderProps {
  children: ReactNode;
}

/** Mount once, above both the topbar and the routed screens. */
export function ScanSlotProvider({ children }: ScanSlotProviderProps) {
  const [stack, setStack] = useState<readonly ScanBoxEntry[]>([]);
  const [listening, setListening] = useState(false);
  const commandsRef = useRef<ScanBoxCommands | null>(null);

  const register = useCallback((entry: ScanBoxEntry) => {
    setStack((previous) => {
      const index = previous.findIndex((candidate) => candidate.id === entry.id);
      // Re-registration (a changed placeholder) updates in place: it must not
      // jump the queue over a nested screen that registered later.
      if (index === -1) return [...previous, entry];
      const next = previous.slice();
      next[index] = entry;
      return next;
    });
  }, []);

  const unregister = useCallback((id: string) => {
    setStack((previous) =>
      previous.some((candidate) => candidate.id === id)
        ? previous.filter((candidate) => candidate.id !== id)
        : previous,
    );
  }, []);

  const setCommands = useCallback((commands: ScanBoxCommands | null) => {
    commandsRef.current = commands;
  }, []);

  const focus = useCallback(() => {
    commandsRef.current?.focus();
  }, []);

  const clear = useCallback(() => {
    commandsRef.current?.clear();
  }, []);

  // Innermost registration wins — a scan-capable dialog over a scan-capable
  // screen takes the box and hands it back on unmount.
  const active = stack.length === 0 ? null : (stack[stack.length - 1] ?? null);

  const store = useMemo<ScanSlotStore>(
    () => ({ active, listening, register, unregister, setCommands, setListening, focus, clear }),
    [active, listening, register, unregister, setCommands, focus, clear],
  );

  return <ScanSlotContext.Provider value={store}>{children}</ScanSlotContext.Provider>;
}

function normalizeHotkeys(hotkey: ScanBoxRegistration["hotkey"]): readonly string[] {
  if (hotkey === null) return EMPTY_HOTKEYS;
  if (hotkey === undefined) return DEFAULT_SCAN_HOTKEYS;
  return typeof hotkey === "string" ? [hotkey] : hotkey;
}

/**
 * Register a scan box in the topbar for as long as this component is mounted.
 *
 *   const scan = useRegisterScanBox({
 *     placeholder: t("pick.scanItem", "Scan item or bin"),
 *     onScan: (value) => resolveBarcode(value),
 *   });
 *
 * Callbacks may be inline — the registry always calls the latest ones. To take
 * the box away, stop rendering the component that calls this hook (hooks are
 * unconditional); to leave it visible but inert, pass `disabled`.
 */
export function useRegisterScanBox(registration: ScanBoxRegistration): ScanBoxHandle {
  const store = useContext(ScanSlotContext);
  const id = useId();

  const configRef = useRef(registration);
  useEffect(() => {
    configRef.current = registration;
  });

  const {
    placeholder,
    label,
    hint,
    disabled = false,
    captureMode = "field",
    hotkey,
  } = registration;

  // Collapse the hotkeys to a primitive so an inline array literal does not
  // re-register the box on every render.
  const hotkeyKey = useMemo(() => normalizeHotkeys(hotkey).join("\u001f"), [hotkey]);
  const hotkeys = useMemo(
    () => (hotkeyKey === "" ? EMPTY_HOTKEYS : hotkeyKey.split("\u001f")),
    [hotkeyKey],
  );

  const { register, unregister } = store;
  useEffect(() => {
    register({
      id,
      config: configRef,
      display: { placeholder, label, hint, hotkeys, disabled, captureMode },
    });
    return () => {
      unregister(id);
    };
  }, [register, unregister, id, placeholder, label, hint, hotkeys, disabled, captureMode]);

  const isActive = store.active?.id === id;
  const { focus, clear, listening } = store;

  return useMemo(
    () => ({ isActive, isListening: isActive && listening, focus, clear }),
    [isActive, listening, focus, clear],
  );
}

/** Read-only view of the slot (for a screen that wants to mirror the listening state). */
export function useScanSlot(): Pick<ScanSlotStore, "listening" | "focus" | "clear"> & {
  isRegistered: boolean;
} {
  const store = useContext(ScanSlotContext);
  return {
    isRegistered: store.active !== null,
    listening: store.listening,
    focus: store.focus,
    clear: store.clear,
  };
}

/* -------------------------------------------------------------------------- */
/* The box                                                                    */
/* -------------------------------------------------------------------------- */

function isEditableElement(element: Element | null): boolean {
  if (element === null) return false;
  const tag = element.tagName;
  if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
  return element instanceof HTMLElement && element.isContentEditable;
}

export interface TopbarScanBoxProps {
  className?: string;
}

/**
 * The rendered slot. Lives in the topbar; renders nothing at all until a
 * screen registers, which is why finance never sees a scan field.
 */
export function TopbarScanBox({ className }: TopbarScanBoxProps) {
  const { t } = useTranslation();
  const store = useContext(ScanSlotContext);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [value, setValue] = useState("");
  const hintId = useId();

  const active = store.active;
  const activeId = active?.id ?? null;
  const disabled = active?.display.disabled ?? false;
  const hotkeys = active?.display.hotkeys ?? EMPTY_HOTKEYS;
  const { setCommands, setListening, listening } = store;

  // Publish focus/clear back to the registrant's handle.
  useEffect(() => {
    if (activeId === null) return;
    setCommands({
      focus: () => {
        inputRef.current?.focus();
        inputRef.current?.select();
      },
      clear: () => {
        setValue("");
        inputRef.current?.focus();
      },
    });
    return () => {
      setCommands(null);
    };
  }, [activeId, setCommands]);

  // A newly registered box starts empty and takes focus: on the floor the first
  // keystroke after a screen opens is a scan, never a click.
  useEffect(() => {
    if (activeId === null || disabled) {
      setListening(false);
      return;
    }
    setValue("");
    inputRef.current?.focus();
    return () => {
      setListening(false);
    };
  }, [activeId, disabled, setListening]);

  useEffect(() => {
    if (activeId === null || disabled || hotkeys.length === 0) return;
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented || event.ctrlKey || event.metaKey || event.altKey) return;
      if (!hotkeys.includes(event.key)) return;
      // A printable hotkey ("/") must never steal a character from a field
      // someone is typing in — including this box itself, where "/" can be part
      // of a barcode. A function key (F2) works from anywhere.
      if (event.key.length === 1 && isEditableElement(document.activeElement)) return;
      event.preventDefault();
      inputRef.current?.focus();
      inputRef.current?.select();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [activeId, disabled, hotkeys]);

  if (active === null) return null;

  const display = active.display;
  const hotkeyLabel = hotkeys[0];

  const submit = (): void => {
    const raw = value;
    const scanned = raw.trim();
    setValue("");
    // Re-focus after every scan: the next barcode must land in the box even if
    // the last one moved focus (a button, a toast, a dialog that closed).
    inputRef.current?.focus();
    if (scanned === "") return;
    // In "external" mode the screen's own capture source already delivered it.
    if (display.captureMode === "external") return;
    active.config.current.onScan(scanned, { raw });
  };

  return (
    <form
      data-slot="scan-slot"
      className={cn("flex shrink-0 items-center gap-2", className)}
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <div className="relative flex items-center">
        <ScanLine
          className="pointer-events-none absolute start-2.5 size-4 text-muted-foreground"
          aria-hidden="true"
        />
        <Input
          ref={inputRef}
          data-slot="scan-input"
          // Barcode payloads are Latin/code runs and must not reorder in RTL.
          dir="ltr"
          value={value}
          disabled={disabled}
          placeholder={display.placeholder ?? t("scanSlot.placeholder", "Scan a barcode")}
          aria-label={display.label ?? t("scanSlot.label", "Barcode scan")}
          aria-describedby={display.hint === undefined ? undefined : hintId}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          enterKeyHint="go"
          className="w-52 ps-8 lg:w-64"
          onChange={(event) => {
            setValue(event.target.value);
          }}
          onFocus={() => {
            setListening(true);
          }}
          onBlur={() => {
            setListening(false);
          }}
          onKeyDown={(event) => {
            if (event.key !== "Escape") return;
            event.preventDefault();
            setValue("");
            active.config.current.onCancel?.();
          }}
        />
      </div>

      <span
        role="status"
        data-slot="scan-status"
        className={cn(
          "hidden items-center gap-1.5 whitespace-nowrap text-xs lg:flex",
          listening && !disabled ? "text-success" : "text-muted-foreground",
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            "size-2 rounded-full",
            listening && !disabled ? "animate-pulse bg-success" : "bg-muted-foreground/40",
          )}
        />
        {listening && !disabled
          ? t("scanSlot.listening", "Listening")
          : hotkeyLabel === undefined
            ? t("scanSlot.idle", "Scanner idle")
            : t("scanSlot.pressToScan", "Press {{key}} to scan", { key: hotkeyLabel })}
      </span>

      {display.hint !== undefined && (
        <span id={hintId} className="sr-only xl:not-sr-only xl:text-xs xl:text-muted-foreground">
          {display.hint}
        </span>
      )}
    </form>
  );
}
